import React from "react";
import api from "./api";
import { CLINIC, CLINIC_DEFAULTS, CLINIC_UPDATED_EVENT, setClinic, resetClinic, applyClinicPayload, documentTitleFor } from "./clinicState";

// The branding state lives in ./clinicState (pure, node-testable). CLINIC is
// MUTATED in place (not re-assigned) so imports across the app read live values
// on every render. It starts as the neutral Prescribify fallback and is replaced
// by the ACTIVE tenant's saved profile via bootstrapClinic() / refreshClinic().
export { CLINIC, CLINIC_DEFAULTS, setClinic, resetClinic, applyClinicPayload, documentTitleFor };

/** MT-11C.4: print/PDF pages — document.title (= PDF Title metadata) follows the
 *  ACTIVE tenant profile + server-derived parts (e.g. signed prescriber). */
export function usePrintTitle(...parts) {
  const clinic = useClinic();
  const key = parts.join("|");
  React.useEffect(() => {
    document.title = documentTitleFor(...parts);
    return () => { document.title = documentTitleFor(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, clinic.name]);
}

// Resolve the doctor whose signature/name should appear on a given Rx.
// Preference order:
//   1. explicit doctor_id passed in (from prescription.signed_by_doctor_id)
//   2. first entry in CLINIC.doctors
//   3. legacy single-doctor fallback (CLINIC.doctorName + qualifications + regNo)
export function resolveDoctor(doctorId) {
  const list = CLINIC.doctors || [];
  if (doctorId) {
    const hit = list.find((d) => d.id === doctorId);
    if (hit) return hit;
  }
  if (list.length) return list[0];
  return {
    id: "d1",
    name: CLINIC.doctorName || "Doctor",
    qualifications: CLINIC.qualifications || "",
    regNo: CLINIC.regNo || "",
    signature_url: "",
  };
}

// Absolute URL helper for logo / signature — prefixes REACT_APP_BACKEND_URL
// so the same relative path works in preview AND production (and survives
// Kubernetes ingress rewrites).
export function absUrl(src) {
  if (!src) return "";
  if (src.startsWith("http")) return src;
  return `${process.env.REACT_APP_BACKEND_URL || ""}${src}`;
}

// Doctor's profile photo — uploaded via Clinic Profile. MT-11C.2: no bundled
// clinic-specific photo may leak into another tenant's pages, so the fallback
// is a neutral inline placeholder.
const NEUTRAL_AVATAR = "data:image/svg+xml;utf8," + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" fill="#e2e8f0"/>' +
  '<circle cx="32" cy="24" r="12" fill="#94a3b8"/><path d="M10 58c2-13 11-20 22-20s20 7 22 20z" fill="#94a3b8"/></svg>');
export function doctorPhotoUrl() {
  return CLINIC.doctor_photo_url ? absUrl(CLINIC.doctor_photo_url) : NEUTRAL_AVATAR;
}


// MT-11C.2: refetch the ACTIVE tenant's profile and REPLACE the branding state
// (previous tenant values never survive). Called after login / clinic selection.
export async function refreshClinic() {
  let data = null;
  try {
    data = (await api.get("/settings/clinic")).data;
  } catch (e) {
    data = null;   // neutral defaults apply — never a stale clinic
  }
  applyClinicPayload(data);
  return !!data;
}

// Fired once on app boot from App.js. Silent on failure — the neutral defaults apply.
let _bootstrapped = false;
export async function bootstrapClinic() {
  if (_bootstrapped) return;
  _bootstrapped = true;
  await refreshClinic();
}

/** Subscribe a component to clinic-profile changes; returns the live CLINIC. */
export function useClinic() {
  const [, force] = React.useReducer((x) => x + 1, 0);
  React.useEffect(() => {
    window.addEventListener(CLINIC_UPDATED_EVENT, force);
    return () => window.removeEventListener(CLINIC_UPDATED_EVENT, force);
  }, []);
  return CLINIC;
}


// Clinic brand colour — configurable in Clinic Profile; strict hex or the
// default JM pink. Used on kit headers/footers and printed-Rx accents.
export const DEFAULT_BRAND = "#DB2777";
export function brandColor() {
  const c = (CLINIC.brand_color || "").trim();
  return /^#[0-9a-fA-F]{6}$/.test(c) ? c : DEFAULT_BRAND;
}
export function brandTint(pct, base = "white") {
  return `color-mix(in srgb, ${brandColor()} ${pct}%, ${base})`;
}

/** Re-tints the Tailwind pink accent classes inside `scope` to the clinic's
 * brand colour. Renders nothing while the clinic keeps the default pink so
 * the stock look stays pixel-identical. */
export function BrandAccentStyle({ scope = "brand-scope" }) {
  const brand = brandColor();
  if (brand.toLowerCase() === DEFAULT_BRAND.toLowerCase()) return null;
  const dark = `color-mix(in srgb, ${brand} 78%, black)`;
  const tint = (p) => `color-mix(in srgb, ${brand} ${p}%, white)`;
  return (
    <style>{`
      .${scope} .text-pink-600, .${scope} .text-pink-700 { color: ${brand} !important; }
      .${scope} .text-pink-800, .${scope} .text-pink-900 { color: ${dark} !important; }
      .${scope} .bg-pink-600 { background-color: ${brand} !important; }
      .${scope} .border-pink-600 { border-color: ${brand} !important; }
      .${scope} .bg-pink-100 { background-color: ${tint(14)} !important; }
      .${scope} .bg-pink-50 { background-color: ${tint(7)} !important; }
      .${scope} .border-pink-200 { border-color: ${tint(30)} !important; }
      .${scope} .border-pink-300 { border-color: ${tint(42)} !important; }
      .${scope} .border-pink-400 { border-color: ${tint(58)} !important; }
      .${scope} .border-pink-500 { border-color: ${tint(78)} !important; }
      .${scope} .from-pink-50 { --tw-gradient-from: ${tint(7)} var(--tw-gradient-from-position) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important; }
      .${scope} .to-pink-50 { --tw-gradient-to: ${tint(7)} var(--tw-gradient-to-position) !important; }
      .${scope} .from-pink-500 { --tw-gradient-from: ${brand} var(--tw-gradient-from-position) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important; }
      .${scope} .to-pink-700 { --tw-gradient-to: ${dark} var(--tw-gradient-to-position) !important; }
      .${scope} .hover\\:bg-pink-700:hover { background-color: ${dark} !important; }
    `}</style>
  );
}

// Legal disclaimer printed on every prescription surface — the paper Rx,
// the patient portal page, and the public authenticity page. Kept as a
// single source of truth so wording stays consistent across all channels
// (screen + print + PDF export) and translated so the patient reads it
// in their own language.
export const RX_LEGAL_DISCLAIMER_I18N = {
  en:
    "This prescription is valid only until the documented follow-up date. " +
    "Please consult the prescribing doctor before continuing or refilling any " +
    "medicine beyond that date. Pharmacists are requested not to dispense " +
    "these medicines after the prescription expires without renewed " +
    "authorization. Seek medical advice promptly if any adverse effect occurs.",
  mr:
    "ही प्रिस्क्रिप्शन नमूद केलेल्या फॉलो-अप तारखेपर्यंतच वैध आहे. " +
    "त्या तारखेनंतर कोणतीही औषधे सुरू ठेवण्यापूर्वी किंवा पुन्हा घेण्यापूर्वी " +
    "कृपया प्रिस्क्रायबिंग डॉक्टरांचा सल्ला घ्या. औषधविक्रेत्यांना विनंती आहे की, " +
    "नवीन अधिकृततेशिवाय प्रिस्क्रिप्शनची मुदत संपल्यानंतर ही औषधे विक्रीस देऊ नयेत. " +
    "कोणताही दुष्परिणाम आढळल्यास त्वरित वैद्यकीय सल्ला घ्या.",
  hi:
    "यह प्रिस्क्रिप्शन दर्ज की गई फॉलो-अप तिथि तक ही मान्य है। " +
    "उस तिथि के बाद कोई भी दवा जारी रखने या दोबारा लेने से पहले कृपया " +
    "निर्धारण करने वाले डॉक्टर से परामर्श लें। दवा विक्रेताओं से अनुरोध है कि " +
    "प्रिस्क्रिप्शन की अवधि समाप्त होने के बाद बिना नए प्राधिकरण के ये दवाइयाँ न दें। " +
    "यदि कोई दुष्प्रभाव हो, तो तुरंत चिकित्सकीय सलाह लें।",
};
// Backwards-compatible alias — existing English-only imports keep working.
export const RX_LEGAL_DISCLAIMER = RX_LEGAL_DISCLAIMER_I18N.en;
export const rxLegalDisclaimer = (lang) => {
  const key = (lang || "en").toLowerCase().slice(0, 2);
  return RX_LEGAL_DISCLAIMER_I18N[key] || RX_LEGAL_DISCLAIMER_I18N.en;
};


export const LOGO_SVG = (
  <svg viewBox="0 0 100 135" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    <rect x="35" y="0" width="30" height="30" fill="#E91E63" />
    <rect x="0" y="35" width="30" height="30" fill="#333333" />
    <rect x="35" y="35" width="30" height="30" fill="#333333" />
    <rect x="70" y="35" width="30" height="30" fill="#333333" />
    <path d="M0 70 H30 V85 C30 93.2843 23.2843 100 15 100 H0 V70 Z" fill="#333333" />
    <rect x="35" y="70" width="30" height="30" fill="#333333" />
    <path d="M70 70 H100 V100 H85 C76.7157 100 70 93.2843 70 85 V70 Z" fill="#333333" />
    <path d="M35 105 H65 V120 C65 128.284 58.2843 135 50 135 H35 V105 Z" fill="#333333" />
  </svg>
);

/** Small square logo tile — prefers the uploaded logo URL (from Clinic
 * Profile settings) and falls back to the built-in SVG. Subscribes to
 * clinic-profile-updated events so it re-renders after Settings save. */
export function LogoTile({ className = "h-8 w-8", padding = "p-1" }) {
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const h = () => setTick((t) => t + 1);
    window.addEventListener("clinic-profile-updated", h);
    return () => window.removeEventListener("clinic-profile-updated", h);
  }, []);
  const src = CLINIC.logo_url;
  // Prepend REACT_APP_BACKEND_URL for a same-origin fetch (works in preview
  // AND production, and survives Kubernetes ingress rewrites).
  const abs = src ? (src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL || ""}${src}`) : "";
  return (
    <div key={tick} className={`${className} ${padding} rounded bg-white border border-slate-200 flex items-center justify-center overflow-hidden`}>
      {abs
        ? <img src={abs} alt={CLINIC.short || CLINIC.name} className="w-full h-full object-contain" />
        : LOGO_SVG}
    </div>
  );
}

/** Format the doctor's display name — never double-prefixes with "Dr." */
export function doctorDisplay(name) {
  const n = (name || "").trim();
  if (!n) return CLINIC.doctorName;
  return /^dr\.?\s/i.test(n) ? n : `Dr. ${n}`;
}

/**
 * Build a WhatsApp-friendly branded message body.
 * Wraps content with a prominent clinic header + doctor credentials + full
 * contact block so anyone the patient forwards it to sees the clinic branding.
 * Uses WhatsApp markdown (*bold*, _italic_) and unicode separators.
 *
 * @param {Object}  opts
 * @param {'en'|'mr'} opts.lang     - 'mr' or 'en'
 * @param {string}  opts.title      - Content title (e.g. diet type, article topic, exercise level)
 * @param {string}  opts.body       - The main content text.
 * @param {string=} opts.patientName - Optional patient name for personalised greeting.
 * @param {string=} opts.kind       - 'diet' | 'exercise' | 'education' — controls tagline.
 * @param {string=} opts.dateStr    - Optional issue date, already formatted.
 */
export function buildWhatsAppBrandedMessage({ lang = "en", title = "", body = "", patientName = "", kind = "education", dateStr = "" }) {
  const isMr = lang === "mr";
  // Short version of the doctor's qualifications for WhatsApp — cap at 90
  // chars so it fits on 1-2 lines on mobile. If a clinic hasn't set qualifications,
  // fall back to the same generic phrase we used before.
  const rawCreds = (CLINIC.qualifications || "").split(",").map((s) => s.trim()).filter(Boolean);
  const shortCreds = rawCreds.length ? rawCreds.slice(0, 3).join(" · ") : "";
  const phones = (CLINIC.phones || []).join(" · ");
  // Compact address — WhatsApp wraps long lines poorly, so keep it 2 lines.
  const addressCompact = (CLINIC.address || "").replace(/,\s+/g, ",\n").split("\n").slice(0, 3).join("\n");

  const kindLabel = isMr
    ? { diet: "वैयक्तिक आहार आराखडा", exercise: "वैयक्तिक व्यायाम आराखडा", education: "आरोग्य शिक्षण" }[kind] || "आरोग्य माहिती"
    : { diet: "Personalised Diet Plan", exercise: "Personalised Exercise Plan", education: "Patient Education" }[kind] || "Health Information";

  const greeting = patientName
    ? (isMr ? `नमस्कार *${patientName}*,` : `Hello *${patientName}*,`)
    : (isMr ? "नमस्कार," : "Hello,");

  const tagline = isMr
    ? `ही माहिती ${CLINIC.name} तर्फे ${doctorDisplay(CLINIC.doctorName)} यांच्या क्लिनिकल पर्यवेक्षणाखाली दिली आहे.`
    : `This information is issued by ${CLINIC.name} under the clinical supervision of ${doctorDisplay(CLINIC.doctorName)}.`;

  const cta = isMr
    ? "अपॉइंटमेंटसाठी संपर्क साधा:"
    : "For appointments, please contact us:";

  const dateLine = dateStr ? (isMr ? `📅 दिनांक: ${dateStr}\n` : `📅 Date: ${dateStr}\n`) : "";

  const header =
    `━━━━━━━━━━━━━━━━━━━━━\n` +
    `🏥 *${CLINIC.name}*\n` +
    `${doctorDisplay(CLINIC.doctorName)}\n` +
    (shortCreds ? `${shortCreds}\n` : "") +
    (CLINIC.regNo ? `${CLINIC.regNo}\n` : "") +
    `━━━━━━━━━━━━━━━━━━━━━\n\n` +
    `${greeting}\n` +
    (title ? `*${kindLabel} — ${title}*\n` : `*${kindLabel}*\n`) +
    dateLine +
    `\n`;

  const footer =
    `\n\n━━━━━━━━━━━━━━━━━━━━━\n` +
    `_${tagline}_\n\n` +
    (addressCompact ? `📍 ${addressCompact}\n` : "") +
    (phones ? `📞 ${phones}\n` : "") +
    (CLINIC.website ? `🌐 ${CLINIC.website}\n` : "") +
    `\n${cta}\n` +
    `━━━━━━━━━━━━━━━━━━━━━`;

  return header + body + footer;
}

/** MT-12B: public Verify-Rx URL. The tenant locator is SERVER-issued
 *  (rx.verify_locator, null for the legacy clinic) — never built client-side. */
export function verifyRxUrl(rx) {
  if (!rx?.verify_token) return "";
  const base = `${window.location.origin}/verify-rx/${rx.verify_token}`;
  return rx.verify_locator ? `${base}?t=${encodeURIComponent(rx.verify_locator)}` : base;
}

/** Append ?lang= to a public link that may already carry the ?t= locator. */
export function withLang(url, lang) {
  if (!url || !lang || lang === "en") return url || "";
  return `${url}${url.includes("?") ? "&" : "?"}lang=${encodeURIComponent(lang)}`;
}
